import { motion } from "framer-motion";

/**
 * Animated "delivery in progress" map.
 * Stylised Trinidad silhouette + waypoint dots + animated driver bubbles
 * gliding along curved paths between cities.
 *
 * Pure SVG, no external map service — investor-ready visual.
 */
export default function LiveDeliveryMap() {
  return (
    <div
      className="relative w-full h-[460px] rounded-2xl overflow-hidden bg-gradient-to-br from-[#0A1626] via-[#0F1F35] to-[#0A1626]"
      data-testid="live-delivery-map"
    >
      {/* Grid backdrop */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.18]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="ihgrid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#14B8A6" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#ihgrid)" />
      </svg>

      {/* Radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_60%,rgba(20,184,166,0.18)_0%,transparent_70%)]" />

      {/* Map + paths */}
      <svg viewBox="0 0 500 460" className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="pathGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#39FF9A" stopOpacity="0.35" />
          </linearGradient>
          <radialGradient id="pinGlow">
            <stop offset="0%" stopColor="#39FF9A" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#14B8A6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Stylised Trinidad silhouette (abstract) */}
        <motion.path
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ duration: 1.2 }}
          d="M 90 130 Q 60 180 90 240 Q 110 300 170 340 Q 240 370 320 355 Q 400 340 430 290 Q 445 240 415 190 Q 380 140 320 120 Q 250 105 190 110 Q 130 115 90 130 Z"
          fill="rgba(20,184,166,0.06)"
          stroke="rgba(20,184,166,0.55)"
          strokeWidth="1.2"
          strokeDasharray="4 4"
        />

        {/* Compass rose small mark */}
        <g transform="translate(60,60)" opacity="0.4">
          <circle r="14" fill="none" stroke="#14B8A6" strokeWidth="0.6" />
          <line x1="0" y1="-14" x2="0" y2="-4" stroke="#14B8A6" strokeWidth="1" />
          <text y="-16" textAnchor="middle" fill="#14B8A6" fontSize="9" fontFamily="monospace">N</text>
        </g>

        {/* Route curves */}
        {ROUTES.map((r, i) => (
          <path
            key={i}
            id={`route-${i}`}
            d={r.d}
            fill="none"
            stroke="url(#pathGrad)"
            strokeWidth="1.6"
            strokeDasharray="3 6"
            opacity="0.7"
          />
        ))}

        {/* Waypoints */}
        {CITIES.map((c, i) => (
          <g key={c.name} transform={`translate(${c.x},${c.y})`}>
            <circle r="18" fill="url(#pinGlow)">
              <animate attributeName="r" values="14;22;14" dur="2.6s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.9;0.2;0.9" dur="2.6s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
            </circle>
            <circle r="4.5" fill="#0A1626" stroke="#39FF9A" strokeWidth="1.4" />
            <circle r="1.6" fill="#39FF9A" />
            <text x="10" y="4" fill="#e5e7eb" fontSize="11" fontFamily="monospace" style={{ letterSpacing: "0.05em" }}>{c.name}</text>
          </g>
        ))}

        {/* Animated driver dots gliding along each route */}
        {ROUTES.map((r, i) => (
          <g key={`d-${i}`}>
            <circle r="6" fill="#F97316" opacity="0.35">
              <animateMotion dur={`${r.dur}s`} repeatCount="indefinite" begin={`${r.delay}s`}>
                <mpath href={`#route-${i}`} />
              </animateMotion>
              <animate attributeName="r" values="6;10;6" dur="1.4s" repeatCount="indefinite" />
            </circle>
            <circle r="3" fill="#F97316">
              <animateMotion dur={`${r.dur}s`} repeatCount="indefinite" begin={`${r.delay}s`}>
                <mpath href={`#route-${i}`} />
              </animateMotion>
            </circle>
          </g>
        ))}
      </svg>

      {/* Live pill top-left */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/10"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80">Live · Trinidad</span>
      </motion.div>

      {/* Counter chip top-right */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute top-4 right-4 px-3 py-2 rounded-xl bg-black/60 backdrop-blur border border-white/10"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Active drivers</div>
        <div className="text-lg font-display font-black text-white leading-none mt-0.5">
          <AnimatedCount from={12} to={47} />
        </div>
      </motion.div>
    </div>
  );
}

const CITIES = [
  { name: "Port of Spain", x: 140, y: 175 },
  { name: "Chaguanas", x: 235, y: 220 },
  { name: "San Fernando", x: 220, y: 305 },
  { name: "Arima", x: 320, y: 195 },
  { name: "Sangre Grande", x: 385, y: 220 },
];

const ROUTES = [
  { d: "M 140 175 Q 200 160 235 220", dur: 3.8, delay: 0 },
  { d: "M 235 220 Q 260 265 220 305", dur: 3.2, delay: 0.9 },
  { d: "M 235 220 Q 280 195 320 195", dur: 3.6, delay: 1.6 },
  { d: "M 320 195 Q 360 200 385 220", dur: 2.8, delay: 2.2 },
  { d: "M 140 175 Q 180 245 220 305", dur: 5.4, delay: 0.3 },
];

const AnimatedCount = ({ from, to }) => {
  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <motion.span
        initial={{ y: 6, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {to}
      </motion.span>
    </motion.span>
  );
};
